import React from 'react';

import { Badge, SearchableDropdown } from '@/components';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';

import { SI_TAG } from './riderFilter';
import { useDriversDropdown } from './useDriversDropdown';

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';

// ─── Client (Klien) picker — O3, backed by GET /v1/providers ─────────────

export interface ClientSelection {
  providerId: number;
  clientName: string;
}

export const ClientPicker: React.FC<{
  open: boolean;
  providerId: number | '';
  clientName: string;
  onChange: (sel: ClientSelection | null) => void;
  disabled?: boolean;
  placeholder?: string;
}> = ({ open, providerId, clientName, onChange, disabled, placeholder }) => {
  const providers = useProvidersDropdown(open, 'SCHEDULED_INSTANT');

  return (
    <SearchableDropdown
      options={providers.options.map((p) => ({ value: p.id, label: p.name }))}
      value={providerId === '' ? undefined : providerId}
      selectedLabelFallback={
        clientName || (providerId !== '' ? providers.labelFor(providerId) : '')
      }
      onChange={(next) => {
        if (!next) {
          onChange(null);
          return;
        }
        const idNum = Number(next);
        const p = providers.options.find((o) => String(o.id) === String(next));
        onChange({
          providerId: idNum,
          clientName: p?.name ?? providers.labelFor(idNum) ?? '',
        });
      }}
      onSearchChange={providers.setSearch}
      disableLocalFilter
      loading={providers.loading}
      hasMore={providers.hasMore}
      onLoadMore={providers.loadMore}
      allowClear
      placeholder={placeholder ?? 'Cari klien Scheduled Instant…'}
      disabled={disabled}
      emptyMessage={providers.error || 'Klien tidak ditemukan'}
    />
  );
};

// ─── Rider picker — O4, backed by GET /v3/drivers ────────────────────────

export interface RiderSelection {
  riderCode: string;
  riderName: string;
  riderPhone: string;
  isDayOff?: boolean;
}

const driverName = (d: {
  name?: string;
  first_name?: string;
  last_name?: string;
}) => d.name || `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim();

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

type RiderTab = 'dedicated' | 'all';

export const RiderPicker: React.FC<{
  open: boolean;
  title: string;
  hint?: string;
  required?: boolean;
  riders: RiderSelection[];
  onChange: (next: RiderSelection[]) => void;
  disabled?: boolean;
}> = ({ open, title, hint, required, riders, onChange, disabled }) => {
  const [tab, setTab] = React.useState<RiderTab>('dedicated');

  // Two data sources, each active only on its tab (avoids double-fetch).
  // Dedicated = server-side tag filter; All = client-side Express hide (D2).
  const dedicated = useDriversDropdown(open && tab === 'dedicated', {
    tagNames: SI_TAG.DEDICATED,
  });
  const all = useDriversDropdown(open && tab === 'all', {
    excludeTag: SI_TAG.EXPRESS,
  });
  const drivers = tab === 'dedicated' ? dedicated : all;

  const add = (id: string) => {
    const d = drivers.options.find((o) => String(o.id) === String(id));
    if (!d) return;
    const code = d.code || `DRV${d.id}`;
    if (riders.some((r) => r.riderCode === code)) return; // no dupes
    const phone =
      d.phone || d.phone_number || d.phoneNumber || d.mobile || '';
    onChange([
      ...riders,
      { riderCode: code, riderName: driverName(d), riderPhone: phone },
    ]);
    drivers.setSearch(''); // reset the search list for the next add
  };

  const remove = (code: string) =>
    onChange(riders.filter((r) => r.riderCode !== code));

  return (
    <div>
      <div className="mb-2 flex items-baseline gap-2">
        <label className={`${labelCls} mb-0`}>
          {title} {required && <span className="text-red-500">*</span>}
        </label>
        {hint && (
          <span className="text-[11px] text-gray-400 normal-case tracking-normal">
            {hint}
          </span>
        )}
      </div>

      {/* Dedicated / All tabs (Screen 9B/9C). */}
      <div className="mb-2 flex items-center gap-1">
        {(
          [
            { key: 'dedicated', label: '🎯 Dedicated Riders' },
            { key: 'all', label: '🌐 All Riders' },
          ] as { key: RiderTab; label: string }[]
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            disabled={disabled}
            onClick={() => setTab(t.key)}
            className={`px-2.5 py-1 rounded-gsm text-[11px] font-semibold transition-colors ${
              tab === t.key
                ? 'bg-(--theme-accent-alpha-10) text-accent'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="mb-2 text-[10.5px] text-gray-400 leading-snug">
        {tab === 'dedicated'
          ? 'Rider bertag "Schedule Instant" — sudah terbiasa dengan klien squad ini.'
          : 'Seluruh rider Dash — rider bertag "Express" disembunyikan otomatis.'}
      </p>

      <SearchableDropdown
        options={drivers.options
          .filter((d) => {
            const code = d.code || `DRV${d.id}`;
            return !riders.some((r) => r.riderCode === code);
          })
          .map((d) => ({
            value: d.id,
            label: `${driverName(d)}${d.code ? ` · ${d.code}` : ''}`,
          }))}
        value={undefined}
        onChange={(next) => next && add(String(next))}
        onSearchChange={drivers.setSearch}
        disableLocalFilter
        loading={drivers.loading}
        hasMore={drivers.hasMore}
        onLoadMore={drivers.loadMore}
        placeholder="Cari & tambah rider…"
        disabled={disabled}
        emptyMessage={drivers.error || 'Rider tidak ditemukan'}
      />

      {tab === 'all' && drivers.hiddenCount > 0 && (
        <p className="mt-1.5 text-[10.5px] text-gray-400 italic">
          +{drivers.hiddenCount} rider Express disembunyikan
        </p>
      )}

      <div className="mt-2 space-y-1.5">
        {riders.length === 0 && (
          <p className="text-[11px] text-gray-400 italic">
            {required
              ? 'Belum ada rider dipilih.'
              : 'Belum ada rider cadangan.'}
          </p>
        )}
        {riders.map((r) => (
          <div
            key={r.riderCode}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-gsm px-3 py-2"
          >
            <span className="w-6 h-6 rounded-full bg-(--theme-accent-alpha-10) text-accent text-[10px] font-bold flex items-center justify-center shrink-0">
              {initials(r.riderName)}
            </span>
            <span className="text-xs font-medium text-ink flex-1 truncate">
              {r.riderName}
            </span>
            {r.isDayOff && (
              <Badge tone="warning" size="small">🌙 Day Off</Badge>
            )}
            <span className="font-mono text-[11px] text-gray-400">
              {r.riderCode}
            </span>
            <span className="font-mono text-[11px] text-gray-400 hidden sm:inline">
              {r.riderPhone || '—'}
            </span>
            <button
              type="button"
              onClick={() => remove(r.riderCode)}
              disabled={disabled}
              className="shrink-0 h-7 w-7 flex items-center justify-center rounded-gsm text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors"
              aria-label={`Hapus ${r.riderName}`}
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
